import { useEffect, useState, useMemo, useCallback } from 'react';
import { addWeeks, startOfDay } from 'date-fns';
import { useCommunication } from '../lib/CommunicationWrapper';

interface GoogleCalendarEvent {
    id: string;
    summary: string;
    description?: string;
    start: {
        dateTime?: string;
        date?: string;
        timeZone?: string;
    };
    end: {
        dateTime?: string;
        date?: string;
        timeZone?: string;
    };
    colorId?: string;
    htmlLink?: string;
    status?: string;
    organizer?: {
        email?: string;
        displayName?: string;
    };
    attendees?: any[];
    location?: string;
    recurringEventId?: string;
    calendarId?: string;
    calendarColor?: string;
}

/**
 * Hook to read Google Calendar events from local storage
 * The extension (bettercanvas-wxt) handles syncing events to local storage
 * Hook consumers can still fetch events on-demand using fetchEventsForDateRange
 */
export const useGoogleCalendarEvents = () => {
    const [events, setEvents] = useState<GoogleCalendarEvent[]>([]);
    const [lastSync, setLastSync] = useState<number | null>(null);

    const { useRerenderListener, sendMessageAndListen } = useCommunication();

    // Watch for changes to cached events in local storage
    const { options } = useRerenderListener(['google_cal_events', 'google_cal_events_last_sync'], 'local');

    // Also watch for token and calendars from sync storage for fetchEventsForDateRange
    const { options: syncOptions } = useRerenderListener(['google_calendars', 'google_cal_sync_token'], 'sync');

    // Process cached events from local storage
    useEffect(() => {
        const cachedEvents = options?.google_cal_events || {};
        const syncTimestamp = options?.google_cal_events_last_sync || null;

        console.log('[useGoogleCalendarEvents] Processing cached events from local storage');

        const allEvents: GoogleCalendarEvent[] = [];

        // Flatten events from all calendars
        for (const calendarEvents of Object.values(cachedEvents)) {
            if (Array.isArray(calendarEvents)) {
                allEvents.push(...calendarEvents);
            }
        }

        // Sort events by start time
        allEvents.sort((a, b) => {
            const aStart = new Date(a.start?.dateTime || a.start?.date || 0);
            const bStart = new Date(b.start?.dateTime || b.start?.date || 0);
            return aStart.getTime() - bStart.getTime();
        });

        console.log(`[useGoogleCalendarEvents] Loaded ${allEvents.length} events from cache`);

        setEvents(allEvents);
        setLastSync(syncTimestamp);
    }, [options?.google_cal_events, options?.google_cal_events_last_sync]);

    // Helper to check and refresh token (calls extension's checkAndRefreshToken via message)
    const getValidToken = useCallback(async () => {
        const response = await sendMessageAndListen({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "checkAndRefreshCalendarTokens",
                args: {}
            }
        });
        if (response.success) return response.token;
        return null;
    }, []);

    // Fetch events for a specific calendar
    const fetchCalendarEvents = useCallback(async (calendarId: string, timeMin: Date, timeMax: Date) => {
        const validToken = await getValidToken();
        console.log('[useGoogleCalendarEvents] validToken', validToken);
        if (!validToken) {
            console.error('[useGoogleCalendarEvents] No valid token available');
            return [];
        }

        try {
            const url = new URL(`https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events`);
            url.searchParams.append('timeMin', timeMin.toISOString());
            url.searchParams.append('timeMax', timeMax.toISOString());
            url.searchParams.append('maxResults', '250');
            url.searchParams.append('singleEvents', 'true');
            url.searchParams.append('orderBy', 'startTime');

            console.log(`[useGoogleCalendarEvents] Fetching from: ${url.toString()}`);

            const response = await fetch(url.toString(), {
                headers: {
                    Authorization: `Bearer ${validToken.access_token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                console.error(`[useGoogleCalendarEvents] Failed to fetch calendar ${calendarId}: ${response.status}`);
                return [];
            }

            const data = await response.json();
            console.log(`[useGoogleCalendarEvents] Fetched ${data.items?.length || 0} events for calendar ${calendarId}`);

            // Filter out Canvas-pushed events
            return (data.items || []).filter((event: any) => {
                const isCanvasPushed = event.extendedProperties?.private?.canvasId ||
                    event.extendedProperties?.private?.canvasType;
                return !isCanvasPushed && event.status !== 'cancelled';
            });
        } catch (error) {
            console.error(`[useGoogleCalendarEvents] Error fetching calendar ${calendarId}:`, error);
            return [];
        }
    }, [getValidToken]);

    // Function to fetch events for a specific date range (on-demand)
    const fetchEventsForDateRange = useCallback(async (date: Date) => {
        try {
            if (!syncOptions?.google_calendars || !syncOptions?.google_cal_sync_token) {
                return [];
            }

            // Calculate a 1-week range starting from the requested date
            const startDate = startOfDay(date);
            const endDate = addWeeks(startDate, 1);

            console.log(`[useGoogleCalendarEvents] Fetching events from ${startDate.toISOString()} to ${endDate.toISOString()}`);

            const allEvents: GoogleCalendarEvent[] = [];

            // Fetch events for each calendar that should sync
            for (const [calendarId, calendar] of Object.entries(syncOptions.google_calendars)) {
                const cal = calendar as any;
                if (!cal.shouldSync) continue;

                const calendarEvents = await fetchCalendarEvents(calendarId, startDate, endDate);

                // Add calendar metadata to each event
                const enrichedEvents = calendarEvents.map((event: any) => ({
                    ...event,
                    calendarId,
                    calendarColor: cal.backgroundColor
                }));

                allEvents.push(...enrichedEvents);
            }

            // Sort events by start time
            allEvents.sort((a, b) => {
                const aStart = new Date(a.start?.dateTime || a.start?.date || 0);
                const bStart = new Date(b.start?.dateTime || b.start?.date || 0);
                return aStart.getTime() - bStart.getTime();
            });

            console.log(`[useGoogleCalendarEvents] Fetched ${allEvents.length} events for range`);
            return allEvents;
        } catch (error) {
            console.error('[useGoogleCalendarEvents] Error fetching date range:', error);
            return [];
        }
    }, [syncOptions?.google_calendars, syncOptions?.google_cal_sync_token, fetchCalendarEvents]);

    // Memoize the return object to prevent unnecessary re-renders
    return useMemo(() => ({
        events,
        loading: false, // Extension handles loading
        lastSync,
        fetchEventsForDateRange,
        refetch: () => { } // Will be implemented via extension
    }), [events, lastSync, fetchEventsForDateRange]);
};

// Helper function to format Google Calendar events for display
export const formatGoogleCalendarEvent = (event: GoogleCalendarEvent) => {
    const startDate = new Date(event.start?.dateTime || event.start?.date || 0);
    const endDate = new Date(event.end?.dateTime || event.end?.date || 0);

    const isAllDay = !event.start?.dateTime;

    return {
        id: event.id,
        title: event.summary || 'Untitled Event',
        description: event.description,
        startDate,
        endDate,
        isAllDay,
        duration: Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60)), // Duration in minutes
        location: event.location,
        htmlLink: event.htmlLink,
        calendarColor: event.calendarColor,
        calendarId: event.calendarId
    };
};
